import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Button from "./Button";
import ContactFormPopup from "./ContactFormPopup";

const Navigation = () => {
  const location = useLocation();
  const navRef = useRef();
  const [showServicesMenu, setShowServicesMenu] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isExpertiseOpen, setIsExpertiseOpen] = useState(false);
  const [popUpActive, setPopupActive] = useState(false);


  // Improved hover handlers for desktop
  const handleServicesMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setShowServicesMenu(true);
  };

  const handleServicesMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowServicesMenu(false);
    }, 150); // Small delay to allow moving to dropdown
    setHoverTimeout(timeout);
  };

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsExpertiseOpen(false);
  };

  const toggleExpertise = () => {
    setIsExpertiseOpen(!isExpertiseOpen);
  };

  // Main navigation items (center navigation)
  const navItems = [
    { path: "/", label: "Home" },
    { path: "/services/1", label: "Expertise", hasDropdown: true },
    { path: "/about", label: "About" },
    // { path: "", label: "Blogs" },
    { path: "/contact", label: "Contact" },
  ];

  // Services dropdown data (3 main categories)
  const servicesDropdownData = [
    {
      id: 1,
      title: "Design",
      subtitle: "Branding & intuitive experience",
      backgroundColor: "bg-white/65 dark:bg-zinc-800/65",
      textColor: "dark:text-white text-black ",
      links: [
        { href: "/design", label: "Design" },
        { href: "/services/1", label: "Graphics" },
        { href: "/services/2", label: "UI/UX" }
      ]
    },
    {
      id: 2,
      title: "Technology",
      subtitle: "Development & digital solutions",
      backgroundColor: "bg-white/65 dark:bg-zinc-800/65",
      textColor: "dark:text-white text-black ",
      links: [
        { href: "/technology", label: "Web & Digital" },
        { href: "/services/3", label: "Web Development" },
        { href: "/services/4", label: "Mobile Apps" }
      ]
    },
    {
      id: 3,
      title: "Marketing",
      subtitle: "Strategy & growth solutions",
      backgroundColor: "bg-white/65 dark:bg-zinc-800/65",
      textColor: "dark:text-white text-black ",
      links: [
        { href: "/marketing", label: "Marketing" },
        { href: "/services/5", label: "Digital Marketing" },
        { href: "/services/6", label: "Content Strategy" }
      ]
    }
  ];

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: navRef.current,
        start: "top -20%",
        end: "top -25%",
        scrub: true,
        // markers: true,
      }
    })
    tl.fromTo(navRef.current, { opacity: 0, y: -50 }, { opacity: 1, y: 0, duration: 0.5 })
  }, [])

  // Base styles for nav links
  const baseLinkStyles = "text-black dark:text-white font-medium text-base transition-colors duration-300 hover:text-[#EE005B] relative";
  const activeLinkStyles = "text-[#EE005B]";
  const inactiveLinkStyles = "text-black dark:text-white";

  // Services dropdown menu component
  const ServicesDropdown = ({ isVisible }) => (
    <div
      className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-2 transition-all duration-300 z-50 ${isVisible ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      style={{ width: '750px' }}
      onMouseEnter={handleServicesMouseEnter}
      onMouseLeave={handleServicesMouseLeave}
    >
      {/* Dropdown container with shadow and border */}
      <div className="grid grid-cols-3 pt-4 ">
        {servicesDropdownData.map((service) => (
          <NavLink
            key={service.id}
            to={service.links[0].href}
            className={` ${service.backgroundColor} backdrop-blur rounded-b-xl w-[250px] p-6 group cursor-pointer transition-all duration-300 block border-b border-x border-zinc-300/20 relative overflow-hidden`}
            onClick={() => setShowServicesMenu(false)}
          >
            { /* Animated gradient background on hover - Different colors for each service */}
            {/* Design Service - Pink Gradient */}
            {service.id === 1 && (
              <>
                <div className="absolute block dark:hidden inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0 bg-[length:150%_150%]"
                    style={{
                      animation: 'gradientMove 3s ease-in-out infinite',
                      backgroundImage: 'radial-gradient(ellipse at bottom, #FF6B9D 0%, #FF8AAD 15%, #FFA8BD 25%, #FFC6CD 35%, #FFE4DD 45%, #FFF2ED 55%, #FFF8F5 65%, #FFFDFC 75%, #ffffff 85%, #ffffff 100%)',
                    }}
                  />
                </div>
                <div className="absolute hidden dark:block inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0 bg-[length:150%_150%]"
                    style={{
                      animation: 'gradientMove 3s ease-in-out infinite',
                      backgroundImage: 'radial-gradient(ellipse at bottom, #EE005B 0%, #C8004D 15%, #A1003F 25%, #7A0031 35%, #531023 45%, #2D0A15 55%, #1A0609 65%, #0D0304 75%, #000000 85%, #000000 100%)',
                    }}
                  />
                </div>
              </>
            )}

            {/* Technology Service - Purple-Blue Gradient */}
            {service.id === 2 && (
              <>
                <div className="absolute block dark:hidden inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0 bg-[length:150%_150%]"
                    style={{
                      animation: 'gradientMove 3s ease-in-out infinite',
                      backgroundImage: 'radial-gradient(ellipse at bottom, #8B5FBF 0%, #9A72C7 15%, #A985CF 25%, #B898D7 35%, #C7ABDF 45%, #D6BEE7 55%, #E5D1EF 65%, #F4E4F7 75%, #ffffff 85%, #ffffff 100%)',
                    }}
                  />
                </div>
                <div className="absolute hidden dark:block inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0 bg-[length:150%_150%]"
                    style={{
                      animation: 'gradientMove 3s ease-in-out infinite',
                      backgroundImage: 'radial-gradient(ellipse at bottom, #6A4C93 0%, #5A3D7A 15%, #4A2E61 25%, #3A1F48 35%, #2A102F 45%, #1D0B20 55%, #120611 65%, #090308 75%, #000000 85%, #000000 100%)',
                    }}
                  />
                </div>
              </>
            )}

            {/* Marketing Service - Light Orange Gradient */}
            {service.id === 3 && (
              <>
                <div className="absolute block dark:hidden inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0 bg-[length:150%_150%]"
                    style={{
                      animation: 'gradientMove 3s ease-in-out infinite',
                      backgroundImage: 'radial-gradient(ellipse at bottom, #FFB366 0%, #FFC085 15%, #FFCDA4 25%, #FFDAC3 35%, #FFE7E2 45%, #FFF1ED 55%, #FFF8F5 65%, #FFFCFA 75%, #ffffff 85%, #ffffff 100%)',
                    }}
                  />
                </div>
                <div className="absolute hidden dark:block inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute inset-0 bg-[length:150%_150%]"
                    style={{
                      animation: 'gradientMove 3s ease-in-out infinite',
                      backgroundImage: 'radial-gradient(ellipse at bottom, #BF6A2A 0%, #A55A24 15%, #8B4A1E 25%, #713A18 35%, #572A12 45%, #3D1A0C 55%, #230A06 65%, #120503 75%, #000000 85%, #000000 100%)',
                    }}
                  />
                </div>
              </>
            )}

            {/* Content wrapper with relative positioning */}
            <div className="relative z-10">
              {/* Header section */}
              <div className="flex items-center justify-between">
                <h3 className={`text-2xl font-bold ${service.textColor} dark:group-hover:text-white duration-300 transition-colors`}>
                  {service.title}
                </h3>
                {/* Arrow icon */}
                <svg
                  className={`w-5 h-5 ${service.textColor} group-hover:translate-x-1 group-hover:text-white duration-300 transition-all`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>

              {/* Subtitle */}
              <p className={`text-sm ${service.textColor} dark:group-hover:text-white/90 opacity-70 group-hover:opacity-100 duration-300 transition-all`}>
                {service.subtitle}
              </p>
            </div>
          </NavLink>
        ))}
      </div>
    </div>
  );

  return (<>
    {/* Add keyframes for gradient animation */}
    <style jsx>{`
      @keyframes gradientMove {
        0% {
          background-position: 50% 0%;
        }
        25% {
          background-position: 50% 50%;
        }
        50% {
          background-position: 50% 100%;
        }
        75% {
          background-position: 50% 50%;
        }
        100% {
          background-position: 50% 0%;
        }
      }
    `}</style>

    <nav className="fixed bg-white dark:bg-black top-0 left-0 right-0 z-50 px-4 xl:px-35 py-4">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <NavLink to="/" className="flex items-center">
          <img src="/new-final-data/logo/Logo_white.gif" className="h-8  dark:block hidden" alt="Logo" />
          <img src="/new-final-data/logo/Logo_black.gif" className="h-8  dark:hidden block" alt="Logo" />
        </NavLink>

        {/* Desktop Navigation - Hidden on mobile/tablet */}
        <div className="hidden xl:flex items-center space-x-8">
          {navItems.map((item) => (
            item.hasDropdown ? (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
              >
                <div className={`${baseLinkStyles} ${location.pathname.includes('/services') ? activeLinkStyles : inactiveLinkStyles} flex items-center cursor-pointer`}>
                  {item.label}
                  {item.hasDropdown && (
                    <svg
                      className={`w-4 h-4 ml-1 transition-transform duration-200 ${showServicesMenu ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
                <ServicesDropdown isVisible={showServicesMenu} />
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `${baseLinkStyles} ${isActive ? activeLinkStyles : inactiveLinkStyles}`
                }
              >
                {item.label}
              </NavLink>
            )
          ))}

          {/* Desktop Enquiry Button */}
          <Link className="scale-[.8]" onClick={() => setPopupActive(true)}>
            <Button>
              Enquiry Now
            </Button>
          </Link>
        </div>

        {/* Mobile Hamburger Menu - Visible on mobile/tablet */}
        <button
          onClick={toggleMobileMenu}
          className="xl:hidden flex flex-col items-center justify-center w-8 h-8 space-y-1 z-60"
        >
          <span className={`block h-0.5 w-6 bg-black dark:bg-white transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
          <span className={`block h-0.5 w-6 bg-black dark:bg-white transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
          <span className={`block h-0.5 w-6 bg-black dark:bg-white transition-transform duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
        </button>
      </div>
    </nav>

    {/* Mobile Menu Overlay */}
    <div
      className={`fixed inset-0 bg-white/50 dark:bg-black/50 backdrop-blur-sm z-40 xl:hidden transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      onClick={closeMobileMenu}
    />

    {/* Mobile Menu Panel */}
    <div
      className={`fixed top-0 left-0 h-full w-[80%] max-w-xl bg-white dark:bg-zinc-900 z-50 xl:hidden transform transition-transform duration-300 border-r border-zinc-200 dark:border-zinc-700 shadow-2xl ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      style={{
        // backgroundImage: 'url(/new-final-data/about/Aboutus.webp)', // You can add your parrot image here
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Background overlay for image */}
      <div className="absolute h-full w-full top-0 left-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-sm"></div>
      {/* Mobile Menu Header */}
      <div className="flex relative z-20 items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-700">
        <NavLink to="/" onClick={closeMobileMenu}>
          <img src="/new-final-data/logo/Logo_white.gif" className="h-8  dark:block hidden" alt="Logo" />
          <img src="/new-final-data/logo/Logo_black.gif" className="h-8  dark:hidden block" alt="Logo" />
        </NavLink>
      </div>

      {/* Mobile Menu Items */}
      <div className="flex relative z-20 flex-col p-6 space-y-2">
        {/* Home */}
        <NavLink
          to="/"
          onClick={closeMobileMenu}
          className={({ isActive }) =>
            `text-2xl font-medium py-3 px-3 rounded-lg transition-all duration-300 ${isActive
              ? 'text-[#EE005B] bg-pink-50 dark:bg-pink-900/20'
              : 'text-zinc-900 dark:text-zinc-100 hover:text-[#EE005B] hover:bg-zinc-50 dark:hover:bg-zinc-800'
            }`
          }
        >
          Home
        </NavLink>

        {/* Expertise with Dropdown */}
        <div className="rounded-lg overflow-hidden bg-zinc-50 dark:bg-zinc-800/50">
          <button
            onClick={toggleExpertise}
            className={`w-full text-left text-2xl font-medium py-3 px-3 transition-all duration-300 flex items-center justify-between ${location.pathname.includes('/services')
              ? 'text-[#EE005B] bg-pink-50 dark:bg-pink-900/20'
              : 'text-zinc-900 dark:text-zinc-100 hover:text-[#EE005B] hover:bg-zinc-100 dark:hover:bg-zinc-700'
              }`}
          >
            Expertise
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isExpertiseOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Expertise Submenu */}
          <div className={`overflow-hidden transition-all duration-300 ${isExpertiseOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="pl-6 pb-3 space-y-1 bg-zinc-100 dark:bg-zinc-800">
              {servicesDropdownData.map((service) => (
                <NavLink
                  key={service.id}
                  to={service.links[0].href}
                  onClick={closeMobileMenu}
                  className="block text-lg font-medium py-2 px-3 rounded-md text-zinc-600 dark:text-zinc-300 hover:text-[#EE005B] hover:bg-white dark:hover:bg-zinc-700 transition-all duration-300"
                >
                  {service.title}
                </NavLink>
              ))}
            </div>
          </div>
        </div>

        {/* Other Menu Items */}
        <NavLink
          to="/about"
          onClick={closeMobileMenu}
          className={({ isActive }) =>
            `text-2xl font-medium py-3 px-3 rounded-lg transition-all duration-300 ${isActive
              ? 'text-[#EE005B] bg-pink-50 dark:bg-pink-900/20'
              : 'text-zinc-900 dark:text-zinc-100 hover:text-[#EE005B] hover:bg-zinc-50 dark:hover:bg-zinc-800'
            }`
          }
        >
          About
        </NavLink>

        <NavLink
          to="/blogs"
          onClick={closeMobileMenu}
          className={({ isActive }) =>
            `text-2xl font-medium py-3 px-3 rounded-lg transition-all duration-300 ${isActive
              ? 'text-[#EE005B] bg-pink-50 dark:bg-pink-900/20'
              : 'text-zinc-900 dark:text-zinc-100 hover:text-[#EE005B] hover:bg-zinc-50 dark:hover:bg-zinc-800'
            }`
          }
        >
          Blogs
        </NavLink>

        <NavLink
          to="/contact"
          onClick={closeMobileMenu}
          className={({ isActive }) =>
            `text-2xl font-medium py-3 px-3 rounded-lg transition-all duration-300 ${isActive
              ? 'text-[#EE005B] bg-pink-50 dark:bg-pink-900/20'
              : 'text-zinc-900 dark:text-zinc-100 hover:text-[#EE005B] hover:bg-zinc-50 dark:hover:bg-zinc-800'
            }`
          }
        >
          Contact
        </NavLink>
      </div>

      {/* Mobile Menu Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
        <Link
          onClick={() => {
            closeMobileMenu();
            setPopupActive(true);
          }}
          className="block w-full mb-4"
        >
          <Button className="w-full">
            Enquiry Now
          </Button>
        </Link>

        {/* Copyright */}
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-4 text-center">
          © 2025, Digicots Agency
        </p>

        {/* Privacy Link */}
        <Link
          to="/privacy"
          onClick={closeMobileMenu}
          className="block text-sm text-zinc-500 dark:text-zinc-400 hover:text-[#EE005B] mt-2 text-center transition-colors duration-300"
        >
          Privacy and Cookie Policy
        </Link>
      </div>
    </div>

    <ContactFormPopup isActive={popUpActive} onClose={() => setPopupActive(false)} />

  </>
  );
};

export default Navigation;
